import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Mail, Lock, User, Cpu, Sparkles, Gift } from 'lucide-react';

export function RegisterPage() {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    try {
      await register({ email, username, password });
      navigate('/login', { state: { message: 'Registration successful! Please log in.' } });
    } catch {
      // Error is handled by the store
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen flex">
      {/* Background effects */}
      <div className="bg-mesh" />
      <div className="bg-noise" />
      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute inset-0 bg-grid opacity-10" />
        
        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8 opacity-0-init animate-fade-in-up">
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-glow">
                <Cpu className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gradient">CipherBin</span>
            </div>
          </div>

          <Card variant="bordered" className="opacity-0-init animate-fade-in-up animation-delay-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Create Account</h2>
              <p className="text-slate-500">Start protecting your software today</p>
            </div>

            {/* Welcome bonus badge */}
            <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 backdrop-blur-sm opacity-0-init animate-fade-in-up animation-delay-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Welcome Bonus</p>
                  <p className="text-purple-400 text-xs">Get 5 free build credits on signup</p>
                </div>
              </div>
            </div>

            {displayError && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm animate-[fade-in-up_0.3s_ease-out]">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-red-400 font-medium">{displayError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        clearError();
                        setLocalError('');
                      }}
                      className="text-red-400/60 text-sm hover:text-red-400 mt-2 underline transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Mail className="w-4 h-4" />}
                required
              />

              <Input
                label="Username"
                type="text"
                placeholder="johndoe"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                icon={<User className="w-4 h-4" />}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<Lock className="w-4 h-4" />}
                required
              />

              <Input
                label="Confirm Password"
                type="password"
                placeholder="••••••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon={<Lock className="w-4 h-4" />}
                required
              />

              <Button type="submit" className="w-full" loading={isLoading} glow>
                <Sparkles className="w-4 h-4" />
                Create Account
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-800/50 text-center">
              <p className="text-slate-500 text-sm">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  onClick={() => {
                    clearError();
                    setLocalError('');
                  }}
                >
                  Sign in
                </Link>
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        
        {/* Floating orbs */}
        <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-purple-500/20 blur-[100px] animate-float" />
        <div className="absolute bottom-1/3 left-1/4 w-96 h-96 rounded-full bg-cyan-500/20 blur-[120px] animate-float" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 right-1/3 w-48 h-48 rounded-full bg-pink-500/15 blur-[80px] animate-float" style={{ animationDelay: '-1.5s' }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="opacity-0-init animate-fade-in-up">
            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 flex items-center justify-center shadow-glow-purple">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 blur-xl opacity-50" />
              </div>
            </div>
            
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              Join the
              <span className="block text-gradient">Revolution</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-md">
              Access powerful protection tools trusted by developers worldwide.
            </p>
            
            {/* Features list */}
            <div className="space-y-4">
              {[
                'Multiple protection engines',
                'Real-time build processing',
                'Secure API access',
              ].map((feature, i) => (
                <div
                  key={feature}
                  className="flex items-center gap-3 opacity-0-init animate-fade-in-up"
                  style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Decorative lines */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
      </div>
    </div>
  );
}
